import { CloseIcon } from "asset/icons";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Avatar from "component/avatar";
import capitalizeWords from "utilities/capitalizeWords";
import { cn } from "../../lib/utils";

const OnboardingLayout = ({
  title = "",
  steps = [],
  currentStep = 0,
  currentSubStep = 0,
  children,
  showSkip = true,
  showStepper = true,
  isUpdating = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { business, users: user } = useSelector((state) => state);

  const accountName = capitalizeWords(
    `${business?.name ?? `${user?.firstName ?? ""} ${user?.lastName ?? ""}`}`
  );
  const accountNameArr = accountName?.split(" ");
  const accountImg =
    accountName === capitalizeWords(business?.name ?? "")
      ? business?.businessImage?.url
      : user?.profileImage?.url;

  const isSendMoneyFlow =
    location.pathname.startsWith("/payment/send-money/") ||
    location.pathname.startsWith("/payment/send-crypto/");

  const handleClose = () => {
    if (isSendMoneyFlow) {
      navigate("/wallets");
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className={cn("!bg-[#f8fafc] w-full", !isUpdating && "min-h-screen")}>
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-inherit justify-between max-md:bg-white h-20 !m-0 !px-6 md:!px-8 !py-0 flex items-center navbar-gradient border-b">
        <div className="flex items-center  justify-between w-full gap-x-3">
          <span className="flex items-center">
            {/* {isSendMoneyFlow ? (
                            <GoArrowLeft className="text-xl text-main-grey mr-2 -translate-y-[4px]" />
                        ) : (
                            <CloseIcon className="*:stroke-main-grey size-[25px] mr-2 -translate-y-[4px]" />
                        )} */}
            <CloseIcon
              onClick={handleClose}
              className="*:stroke-main-grey cursor-pointer size-[25px] mr-2 -translate-y-[4px]"
            />
            <h2 className="text-base font-medium text-main-dark">{title}</h2>
          </span>

          {steps?.length ? null : !isUpdating && <div className="flex items-center justify-between p-2 bg-transparent gap-x-2">
            <Avatar
              img={accountImg}
              className={`${accountNameArr?.length > 3 && "text-[10px] "
                } size-[42px]`}
              fullName={accountName}
            />
            <div className="flex-col items-start justify-center hidden md:flex">
              <p className="text-sm text-normal overflow-clip whitespace-nowrap text-ellipsis max-w-[130px]">
                {accountName}
              </p>
              <span className="text-xs font-light text-[#6F739B]">
                {capitalizeWords(business?.status)}
              </span>
            </div>
          </div>}
        </div>

        {showSkip && (
          <button
            onClick={() => {
              navigate("/dashboard");
              window.location.reload();
            }}
            className="text-sm text-[#7F56D9] whitespace-nowrap hover:underline font-medium"
          >
            Skip for now
          </button>
        )}
      </nav>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar Stepper */}
        {showStepper && <aside className="hidden md:flex flex-col top-20 !pl-20 fixed min-w-[220px] px-8 pt-8">
          {steps.map((step, index) => (
            <div key={index} className="relative flex gap-3 pb-8 last:pb-0">
              {/* Circle and Line */}
              <div className="flex flex-col items-center relative">
                {/* Circle */}
                <span
                  className={`size-7 rounded-full flex items-center justify-center text-sm font-semibold ${index === currentStep
                    ? "bg-[#6F00FF] text-white"
                    : index < currentStep
                      ? "bg-[#E9D7FE] text-[#7F56D9]"
                      : "bg-[#D0D5DD] text-white"
                    }`}
                >
                  {index + 1}
                </span>

                {/* Line to next */}
                {index !== steps.length - 1 && (
                  <div
                    className={`absolute top-7 w-px h-[calc(100%+0.75rem)] ${index < currentStep ? "bg-[#E9D7FE]" : "bg-[#D0D5DD]"
                      }`}
                  />
                )}
              </div>

              {/* Titles & Subtitles */}
              <div className="flex flex-col pt-[5px] gap-2">
                <p
                  className={`text-sm font-semibold ${index === currentStep
                    ? "text-[#101828]"
                    : index < currentStep
                      ? "text-[#344054]"
                      : "text-[#98A2B3]"
                    }`}
                >
                  {step.title}
                </p>

                {/* Subtitles with dot */}
                {step.subtitles && step.subtitles.length > 0 && (
                  <div className="flex flex-col gap-1 pl-[14px] relative">
                    {step.subtitles.map((sub, subIndex) => (
                      <div
                        key={subIndex}
                        className="flex relative items-center gap-2"
                      >
                        {/* Small dot */}
                        <span
                          className={`w-2 h-2 absolute left-[-48px] rounded-full ${index === currentStep &&
                            subIndex === currentSubStep
                            ? "bg-[#6F00FF]"
                            : "bg-[#D0D5DD]"
                            }`}
                        ></span>
                        <p
                          className={`text-sm ${index === currentStep &&
                            subIndex === currentSubStep
                            ? "text-[#6F00FF] font-semibold"
                            : "text-[#667085]"
                            }`}
                        >
                          {sub}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </aside>}

        {/* Right content */}
        <main className={cn("w-full flex-1 h-full", !isUpdating && "md:px-12 md:py-8  p-6")}>{children}</main>
      </div>
    </div>
  );
};

export default OnboardingLayout;
